import React from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { OrgUser } from "../../data/service/tekkr-service-types";
import { useCurrentOrgUser, useOrgController } from "../../auth/org-provider";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import Spinner from "../ui/spinner";
import { useUpdateOrgUserMutation } from "../../data/mutations/update-org-user";
import { useCreateOrgUserMutation } from "../../data/mutations/create-org-user";
import { isApiError } from "../../data/utils";
import { Persona, personaSchema, personaDisplayNames } from "tekkr-common/dist/model/user/persona";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

type PropsType = React.PropsWithChildren & {
    onPersonCreated?: (id: string) => void;
    context: "playbook" | "org";
    editMode?: {
        user: OrgUser;
    };
};

const createFormSchema = z.object({
    name: z.string().min(2, "Please enter a name").max(50, "This name is too long"),
    title: z.string().max(50, "This title is too long").optional(),
    department: z.string().max(50, "This department name is too long").optional(),
    email: z.string().max(100, "This email address is too long").email("Please enter a valid email address"),
    sendInvite: z.boolean(),
});

const editFormSchema = z.object({
    name: z.string().min(2, "Please enter a name").max(50, "This name is too long"),
    title: z.string().max(50, "This title is too long").optional(),
    department: z.string().max(50, "This department name is too long").optional(),
    persona: personaSchema.optional(),
});

type CreateFormType = z.infer<typeof createFormSchema>;
type EditFormType = z.infer<typeof editFormSchema>;

function getErrorMessage(e: Error, isEditMode: boolean) {
    if (isApiError(e)) {
        if (e.status === 409) {
            return "A member with this email address already exists.";
        }
    }
    return isEditMode
        ? "Updating this person failed, please try again later."
        : "Creating this person failed, please try again later.";
}

function PersonDialogContent(props: PropsType & { updateKey: () => void }) {
    const orgController = useOrgController();
    const currentUser = useCurrentOrgUser();
    const isEditMode = !!props.editMode;
    const isEditingSelf = isEditMode && props.editMode!.user.id === currentUser.id;

    async function onSaved() {
        await orgController.refetch();
        resetDialog();
    }

    const editUserMutation = useUpdateOrgUserMutation({ onSuccess: onSaved });
    const createUserMutation = useCreateOrgUserMutation({
        onSuccess: (user) => {
            onSaved();
            if (props.onPersonCreated) {
                props.onPersonCreated(user.id);
            }
        },
    });

    const mutation = useMutation({
        mutationFn: async (formData: CreateFormType | EditFormType) => {
            if (isEditMode) {
                await editUserMutation.mutateAsync({
                    ...(formData as EditFormType),
                    userId: props.editMode!.user.id,
                });
            } else {
                await createUserMutation.mutateAsync(formData as CreateFormType);
            }
        },
    });

    const form = useForm<CreateFormType | EditFormType>({
        resolver: zodResolver(isEditMode ? editFormSchema : createFormSchema),
        defaultValues: isEditMode
            ? {
                  name: props.editMode!.user.name,
                  title: props.editMode!.user.title || "",
                  department: props.editMode!.user.department || "",
                  persona: props.editMode!.user.persona,
              }
            : {
                  name: "",
                  email: "",
                  department: "",
                  sendInvite: props.context === "org",
              },
    });
    function onSubmit(values: CreateFormType | EditFormType) {
        mutation.mutate(values);
    }

    function resetDialog() {
        props.updateKey();
    }

    const [open, setOpenState] = React.useState(false);
    const setOpen = (open: boolean) => {
        if (mutation.isPending) return;
        if (!open) {
            resetDialog();
        }
        setOpenState(open);
    };

    const values = form.getValues();

    return (
        <Dialog open={open} onOpenChange={(open) => setOpen(open)}>
            {props.children}
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className={"text-start"}>
                        {isEditMode ? "Edit Organization Member" : "Add Organization Member"}
                    </DialogTitle>
                    <DialogDescription className={"text-start"}>
                        {isEditMode
                            ? "Update member information"
                            : props.context === "playbook"
                              ? "Add a member to your organization to reference them here and in other places."
                              : `Add a member to your organization and give them access to your projects.`}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem className={"grid grid-cols-5 items-center"}>
                                    <FormLabel className={"pe-2"}>Name</FormLabel>
                                    <FormControl className={"col-span-4"}>
                                        <Input
                                            disabled={mutation.isPending}
                                            autoComplete={"off"}
                                            placeholder="John Doe"
                                            {...field}
                                        />
                                    </FormControl>
                                    <div></div>
                                    <FormMessage className={"col-span-4 mt-2"} />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                                <FormItem className={"grid grid-cols-5 items-center"}>
                                    <FormLabel className={"block pe-2"}>
                                        <div>
                                            Title
                                            <br />
                                            <p className={"mt-1 text-xs text-muted-foreground"}>(optional)</p>
                                        </div>
                                    </FormLabel>
                                    <FormControl className={"col-span-4"}>
                                        <Input
                                            disabled={mutation.isPending}
                                            autoComplete={"off"}
                                            placeholder="Staff Product Manager"
                                            {...field}
                                        />
                                    </FormControl>
                                    <div></div>
                                    <FormMessage className={"col-span-4 mt-2"} />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="department"
                            render={({ field }) => (
                                <FormItem className={"grid grid-cols-5 items-center"}>
                                    <FormLabel className={"block pe-2"}>
                                        <div>
                                            Department
                                            <br />
                                            <p className={"mt-1 text-xs text-muted-foreground"}>(optional)</p>
                                        </div>
                                    </FormLabel>
                                    <FormControl className={"col-span-4"}>
                                        <Input
                                            disabled={mutation.isPending}
                                            autoComplete={"off"}
                                            placeholder="Engineering"
                                            {...field}
                                        />
                                    </FormControl>
                                    <div></div>
                                    <FormMessage className={"col-span-4 mt-2"} />
                                </FormItem>
                            )}
                        />
                        {isEditingSelf && props.editMode?.user.persona && (
                            <FormField
                                control={form.control}
                                name="persona"
                                render={({ field }) => (
                                    <FormItem className={"grid grid-cols-5 items-center"}>
                                        <FormLabel className={"block pe-2"}>
                                            <div>
                                                Persona
                                                <br />
                                                <p className={"mt-1 text-xs text-muted-foreground"}>(optional)</p>
                                            </div>
                                        </FormLabel>
                                        <FormControl className={"col-span-4"}>
                                            <div className="w-full">
                                                <Select
                                                    disabled={mutation.isPending}
                                                    value={field.value ?? undefined}
                                                    onValueChange={(v) => field.onChange(v as Persona)}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue
                                                            placeholder={
                                                                <span className={"text-muted-foreground"}>
                                                                    Select your persona
                                                                </span>
                                                            }
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {personaSchema.options.map((persona) => (
                                                            <SelectItem key={persona} value={persona}>
                                                                {personaDisplayNames[persona]}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </FormControl>
                                        <div></div>
                                        <FormMessage className={"col-span-4 mt-2"} />
                                    </FormItem>
                                )}
                            />
                        )}
                        {isEditMode ? (
                            <FormItem className={"grid grid-cols-5 items-center"}>
                                <FormLabel className={"pe-2"}>Email</FormLabel>
                                <FormControl className={"col-span-4"}>
                                    <Input value={props.editMode!.user.email} disabled={true} />
                                </FormControl>
                            </FormItem>
                        ) : (
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem className={"grid grid-cols-5 items-center"}>
                                        <FormLabel className={"pe-2"}>Email</FormLabel>
                                        <FormControl className={"col-span-4"}>
                                            <Input
                                                disabled={mutation.isPending}
                                                autoComplete={"off"}
                                                placeholder="john@company.com"
                                                {...field}
                                            />
                                        </FormControl>
                                        <div></div>
                                        <FormMessage className={"col-span-4 mt-2"} />
                                    </FormItem>
                                )}
                            />
                        )}
                        {!isEditMode && (
                            <FormField
                                control={form.control}
                                name={"sendInvite"}
                                render={({ field }) => (
                                    <div className={"grid grid-cols-5 items-center"}>
                                        <div></div>
                                        <FormItem className={"col-span-4 flex flex-row items-center gap-2 space-y-0"}>
                                            <FormControl className={"col-span-4"}>
                                                <Checkbox
                                                    disabled={mutation.isPending}
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                            <FormLabel className={"mt-0"}>
                                                Send an invitation email to join Tekkr
                                            </FormLabel>
                                        </FormItem>
                                    </div>
                                )}
                            />
                        )}
                        <div className={"flex flex-row justify-end pt-4"}>
                            <Button type={"submit"} disabled={mutation.isPending}>
                                {!mutation.isPending &&
                                    (isEditMode
                                        ? "Save"
                                        : (values as CreateFormType).sendInvite
                                          ? "Save and Invite"
                                          : "Save")}
                                {mutation.isPending && <Spinner />}
                            </Button>
                        </div>
                    </form>
                </Form>

                {mutation.isError ? (
                    <div className={"rounded-md border border-destructive px-4 py-2 text-sm text-destructive"}>
                        {getErrorMessage(mutation.error, isEditMode)}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

export default function PersonDialog(props: PropsType) {
    const [key, setKey] = React.useState(Math.random());
    return <PersonDialogContent key={key} updateKey={() => setKey(Math.random())} {...props} />;
}
